interface EventFrequencyData {
  eventName: string;
  count: number;
  lastOccurrence: number;
  averageInterval: number;
  maxInterval: number;
}

interface PerformanceMetrics {
  eventFrequencies: Map<string, EventFrequencyData>;
  pageLoadTime: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  cumulativeLayoutShift: number;
  timeToInteractive: number;
  memoryUsage: number;
  apiCallCount: number;
  apiErrorCount: number;
  averageApiResponseTime: number;
}

interface ApiCallMetrics {
  endpoint: string;
  method: string;
  status: number;
  duration: number;
  timestamp: number;
  size: number;
}

class PerformanceMonitor {
  private eventFrequencies: Map<string, EventFrequencyData> = new Map();
  private apiCalls: ApiCallMetrics[] = [];
  private performanceMetrics: PerformanceMetrics;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private initialized = false;

  constructor() {
    this.performanceMetrics = {
      eventFrequencies: new Map(),
      pageLoadTime: 0,
      firstContentfulPaint: 0,
      largestContentfulPaint: 0,
      cumulativeLayoutShift: 0,
      timeToInteractive: 0,
      memoryUsage: 0,
      apiCallCount: 0,
      apiErrorCount: 0,
      averageApiResponseTime: 0,
    };
  }

  initialize(): void {
    if (this.initialized) return;
    if (typeof window === "undefined") return;

    this.collectWebVitals();
    this.setupApiMonitoring();
    this.startPeriodicMonitoring();

    this.initialized = true;
  }

  private collectWebVitals(): void {
    if (typeof window === "undefined") return;

    const navigationTiming = window.performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming;

    if (navigationTiming) {
      this.performanceMetrics.pageLoadTime = Math.round(
        navigationTiming.loadEventEnd - navigationTiming.loadEventStart,
      );
    }

    if ("PerformanceObserver" in window) {
      try {
        const observer = new PerformanceObserver(
          (list: PerformanceObserverEntryList) => {
            list.getEntries().forEach((entry: PerformanceEntry) => {
              if (entry.name === "first-contentful-paint") {
                this.performanceMetrics.firstContentfulPaint = Math.round(
                  entry.startTime,
                );
              }
            });
          },
        );

        observer.observe({ entryTypes: ["paint"] });
      } catch (e) {
        console.error("PerformanceObserver error:", e);
      }

      try {
        const loObserver = new PerformanceObserver(
          (list: PerformanceObserverEntryList) => {
            const entries = list.getEntries();
            if (entries.length > 0) {
              const lastEntry = entries[entries.length - 1];
              this.performanceMetrics.largestContentfulPaint = Math.round(
                lastEntry.startTime,
              );
            }
          },
        );

        loObserver.observe({ entryTypes: ["largest-contentful-paint"] });
      } catch (e) {
        console.error("LCP Observer error:", e);
      }

      try {
        const clsObserver = new PerformanceObserver(
          (list: PerformanceObserverEntryList) => {
            list.getEntries().forEach((entry: PerformanceEntry) => {
              const entryData = entry as unknown as {
                hadRecentInput?: boolean;
                value?: number;
              };
              if (!("hadRecentInput" in entry) || !entryData.hadRecentInput) {
                const firstSessionEntry =
                  this.performanceMetrics.cumulativeLayoutShift;
                this.performanceMetrics.cumulativeLayoutShift =
                  firstSessionEntry + (entryData.value || 0);
              }
            });
          },
        );

        clsObserver.observe({ entryTypes: ["layout-shift"] });
      } catch (e) {
        console.error("CLS Observer error:", e);
      }
    }
  }

  private setupApiMonitoring(): void {
    if (typeof window === "undefined") return;

    const originalFetch = window.fetch;

    window.fetch = async (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> => {
      const startTime = performance.now();
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : (input as Request).url;
      const method = init?.method || "GET";

      try {
        const response = await originalFetch(input, init);
        const duration = performance.now() - startTime;

        this.recordApiCall({
          endpoint: url,
          method,
          status: response.status,
          duration: Math.round(duration),
          timestamp: Date.now(),
          size: response.headers.get("content-length")
            ? parseInt(response.headers.get("content-length") || "0", 10)
            : 0,
        });

        if (response.status >= 400) {
          this.performanceMetrics.apiErrorCount++;
        }

        return response;
      } catch (error) {
        const duration = performance.now() - startTime;

        this.recordApiCall({
          endpoint: url,
          method,
          status: 0,
          duration: Math.round(duration),
          timestamp: Date.now(),
          size: 0,
        });

        this.performanceMetrics.apiErrorCount++;
        throw error;
      }
    };
  }

  private recordApiCall(metrics: ApiCallMetrics): void {
    this.apiCalls.push(metrics);
    this.performanceMetrics.apiCallCount++;

    const totalDuration = this.apiCalls.reduce(
      (sum, call) => sum + call.duration,
      0,
    );
    this.performanceMetrics.averageApiResponseTime = Math.round(
      totalDuration / this.apiCalls.length,
    );

    if (this.apiCalls.length > 1000) {
      this.apiCalls.shift();
    }
  }

  private startPeriodicMonitoring(): void {
    if (typeof window === "undefined") return;

    this.monitoringInterval = setInterval(() => {
      this.collectMemoryMetrics();
    }, 30000);
  }

  private collectMemoryMetrics(): void {
    if (typeof window !== "undefined") {
      const perf = performance as unknown as {
        memory?: { usedJSHeapSize: number };
      };
      if (perf.memory) {
        this.performanceMetrics.memoryUsage =
          Math.round((perf.memory.usedJSHeapSize / 1048576) * 100) / 100;
      }
    }
  }

  trackEvent(eventName: string): void {
    const now = Date.now();
    const existing = this.eventFrequencies.get(eventName);

    if (existing) {
      const interval = now - existing.lastOccurrence;
      existing.count++;
      existing.lastOccurrence = now;
      existing.maxInterval = Math.max(existing.maxInterval, interval);
      existing.averageInterval =
        (existing.averageInterval * (existing.count - 1) + interval) /
        existing.count;
    } else {
      this.eventFrequencies.set(eventName, {
        eventName,
        count: 1,
        lastOccurrence: now,
        averageInterval: 0,
        maxInterval: 0,
      });
    }

    this.performanceMetrics.eventFrequencies = this.eventFrequencies;
  }

  getEventFrequencies(): Record<string, EventFrequencyData> {
    const result: Record<string, EventFrequencyData> = {};

    this.eventFrequencies.forEach((data, name) => {
      result[name] = { ...data };
    });

    return result;
  }

  getPerformanceMetrics(): PerformanceMetrics {
    return {
      ...this.performanceMetrics,
      eventFrequencies: new Map(this.eventFrequencies),
    };
  }

  getApiMetrics() {
    const last24h = this.apiCalls.filter(
      (call) => call.timestamp > Date.now() - 24 * 60 * 60 * 1000,
    );

    const errorCount = last24h.filter(
      (call) => call.status >= 400 || call.status === 0,
    ).length;
    const avgDuration =
      last24h.length > 0
        ? Math.round(
            last24h.reduce((sum, call) => sum + call.duration, 0) /
              last24h.length,
          )
        : 0;

    const topEndpoints = Array.from(
      last24h.reduce((map, call) => {
        const key = `${call.method} ${call.endpoint}`;
        const existing = map.get(key) || { count: 0, totalDuration: 0 };
        existing.count++;
        existing.totalDuration += call.duration;
        map.set(key, existing);
        return map;
      }, new Map<string, { count: number; totalDuration: number }>()),
    )
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 5)
      .map(([endpoint, data]) => ({
        endpoint,
        count: data.count,
        avgDuration: Math.round(data.totalDuration / data.count),
      }));

    return {
      totalCalls: last24h.length,
      errorCount,
      errorRate:
        last24h.length > 0
          ? Math.round((errorCount / last24h.length) * 100)
          : 0,
      averageDuration: avgDuration,
      topEndpoints,
    };
  }

  getHealthStatus(): {
    status: "healthy" | "warning" | "critical";
    issues: string[];
  } {
    const issues: string[] = [];

    if (this.performanceMetrics.pageLoadTime > 3000) {
      issues.push("Page load time > 3s");
    }

    if (this.performanceMetrics.firstContentfulPaint > 2000) {
      issues.push("FCP > 2s");
    }

    if (this.performanceMetrics.largestContentfulPaint > 4000) {
      issues.push("LCP > 4s");
    }

    if (this.performanceMetrics.cumulativeLayoutShift > 0.1) {
      issues.push("CLS > 0.1");
    }

    if (this.performanceMetrics.averageApiResponseTime > 1000) {
      issues.push("Average API response > 1s");
    }

    if (this.performanceMetrics.apiErrorCount > 10) {
      issues.push("API error count > 10");
    }

    if (this.performanceMetrics.memoryUsage > 256) {
      issues.push("Memory usage > 256MB");
    }

    if (issues.length >= 4) {
      return { status: "critical", issues };
    } else if (issues.length > 0) {
      return { status: "warning", issues };
    }

    return { status: "healthy", issues: [] };
  }

  reset(): void {
    this.eventFrequencies.clear();
    this.apiCalls = [];
    this.performanceMetrics = {
      eventFrequencies: new Map(),
      pageLoadTime: 0,
      firstContentfulPaint: 0,
      largestContentfulPaint: 0,
      cumulativeLayoutShift: 0,
      timeToInteractive: 0,
      memoryUsage: 0,
      apiCallCount: 0,
      apiErrorCount: 0,
      averageApiResponseTime: 0,
    };
  }

  destroy(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }
}

export const performanceMonitor = new PerformanceMonitor();

export function usePerformanceMonitoring() {
  if (typeof window !== "undefined") {
    performanceMonitor.initialize();
  }

  return {
    trackEvent: (eventName: string) => performanceMonitor.trackEvent(eventName),
    getMetrics: () => performanceMonitor.getPerformanceMetrics(),
    getApiMetrics: () => performanceMonitor.getApiMetrics(),
    getHealthStatus: () => performanceMonitor.getHealthStatus(),
    getEventFrequencies: () => performanceMonitor.getEventFrequencies(),
  };
}
